import { useMemo } from 'react';
import { useClientId } from '../../../../../../../../_hooks/useClientId';
import { useFpId } from '../../../../../../../hooks/useFpId';
import { useLongevityAnalysisQuery } from '../../../../../../api/useLongevityAnalysisQuery';
import { defaultGender } from '../../LongevityAnalysis.meta';

const getHeader = ({
  principalName,
  spouseName,
  principalGender,
  spouseGender,
}: {
  principalName: string;
  principalGender: string;
  spouseName: string | null;
  spouseGender?: string;
}) => {
  const principalGenderLetter = principalGender !== defaultGender ? `(${principalGender[0]})` : '';

  const baseText = `Ages: ${principalName}${principalGenderLetter}`;

  if (!spouseName) {
    return baseText;
  }

  if (!spouseGender) {
    return baseText;
  }

  const spouseGenderLetter = spouseGender !== defaultGender ? `(${spouseGender[0]})` : '';

  return `${baseText}, ${spouseName}${spouseGenderLetter}`;
};

const getTooltip = ({
  spouseName,
  principalGender,
  spouseGender,
}: {
  principalGender: string;
  spouseName: string | null;
  spouseGender?: string;
}) => {
  const baseText =
    principalGender !== defaultGender ? `${principalGender[0]} - ${principalGender} ` : '';

  if (!spouseName) {
    return baseText;
  }

  if (!spouseGender) {
    return baseText;
  }

  return `${baseText ? `${baseText}, ` : ''}${
    spouseGender !== defaultGender ? `${spouseGender[0]} - ${spouseGender}` : ''
  }`;
};

export const useTable = () => {
  const clientId = useClientId();

  const fpId = useFpId();

  const queryInfo = useLongevityAnalysisQuery({
    clientId,
    fpId,
  });

  const data = useMemo(() => {
    if (!queryInfo.data) {
      return null;
    }

    const {
      PrincipalName: principalName,
      SpouseName: spouseName,
      ChartData,
      PrincipalGender: principalGender,
      SpouseGender: spouseGender,
    } = queryInfo.data;

    const hasSpouse = !!spouseName;

    const headers = [
      {
        header: getHeader({
          principalName,
          principalGender,
          spouseName,
          spouseGender,
        }),
        tooltip: getTooltip({
          principalGender,
          spouseName,
          spouseGender,
        }),
      },
      { header: 'Calendar Year' },
      {
        header: hasSpouse ? 'Probability of one spouse being alive' : 'Probability of being alive',
      },
      ...(hasSpouse ? [{ header: 'Probability of both spouses being alive' }] : []),
      { header: 'Probability of Success (as defined by Monte Carlo Analysis)' },
    ];

    return {
      tableData: ChartData,
      headers,
      principalGender,
      spouseGender,
      hasSpouse,
      principalName,
      spouseName,
    };
  }, [queryInfo.data]);

  return {
    ...queryInfo,
    data,
  };
};
